import { ApplicationDetailedLifecycle } from '../constants/detailedLifecyle';
import { CarrierRoles, Roles } from '../constants/roles';

export const CASE_LOCK_BANNER_ASSIGNED =
  'This case is locked to the assigned Underwriter';
export const CASE_LOCK_BANNER_UNASSIGNED =
  'This case is locked. No underwriter assigned.';

const CLR_EXEMPT_LIFECYCLES = new Set<string>([
  ApplicationDetailedLifecycle.CARRIER_AUDIT_REVIEW,
  ApplicationDetailedLifecycle.FINAL_AUDIT_REVIEW,
  ApplicationDetailedLifecycle.HOLDING_FOR_RESCISSION,
]);

/**
 * V1 roles subject to the lock. A user holding any other V1 role may act on any
 * case; RBAC governs what that role can do.
 */
const CASE_LOCK_V1_SUBJECT_ROLES = new Set<string>([
  Roles.UNDERWRITER,
  Roles.PRODUCTION_UNDERWRITER,
  Roles.UW_JUNIOR_UNDERWRITER,
  Roles.UW_FLEX_UNDERWRITER,
]);

/**
 * V2 roles subject to the lock. A user holding any other V2 role may act on any
 * case; RBAC governs what that role can do.
 */
const CASE_LOCK_V2_SUBJECT_ROLES = new Set<string>([
  Roles.WORKBENCH_UNDERWRITER,
  Roles.WORKBENCH_JUNIOR_UNDERWRITER,
]);

export type CaseLockAccessInput = {
  isFeatureEnabled: boolean;
  userId: number | null;
  userRoles: string[];
  underwriterId: number;
  applicationDetailedLifecycle: string | undefined;
};

export type CaseLockAccess = {
  isFeatureEnabled: boolean;
  isExemptLifecycle: boolean;
  isCarrierUser: boolean;
  hasNonSubjectRole: boolean;
  isAssignedToCurrentUser: boolean;
  isUnassigned: boolean;
  isLockedForActions: boolean;
  bannerMessage: string | null;
};

export function isCaseLockExemptLifecycle(
  applicationDetailedLifecycle: string | undefined,
): boolean {
  return (
    !!applicationDetailedLifecycle &&
    CLR_EXEMPT_LIFECYCLES.has(applicationDetailedLifecycle)
  );
}

function isV2Role(role: string): boolean {
  return role.startsWith('WORKBENCH_');
}

const KNOWN_NORA_ROLES = new Set<string>(Object.values(Roles));

/**
 * `auth.roles` is the raw Okta `groups` claim, not the Nora roles saved in the
 * database, so it also carries non-Nora groups such as `Everyone` (every Okta
 * user is in it). A Nora role is one the frontend knows (`Roles`) or one using
 * the V1 `UW_*` / V2 `WORKBENCH_*` prefix, so a role the frontend list has not
 * caught up with still counts.
 */
function isNoraRole(role: string): boolean {
  return KNOWN_NORA_ROLES.has(role) || isV2Role(role) || role.startsWith('UW_');
}

/**
 * Returns true if the user holds a Nora role outside the case-lock subject
 * lists, so the assignment lock does not apply to them. This is a case-lock rule
 * only; it does not say the role is privileged, and RBAC still governs what it
 * can do.
 *
 * The lock applies only when every Nora role the user holds is a subject role
 * of its system (WORKBENCH_* roles are V2, every other Nora role is V1). Holding
 * any Nora role outside the subject lists, in either system, lifts the lock.
 * Okta groups that are not Nora roles (e.g. `Everyone`) are ignored. A user with
 * no Nora roles is locked.
 */
export function hasNonSubjectCaseLockRole(userRoles: string[]): boolean {
  const noraRoles = userRoles.filter(isNoraRole);
  const v2Roles = noraRoles.filter(isV2Role);
  const v1Roles = noraRoles.filter(role => !isV2Role(role));

  const hasV1NonSubjectRole = v1Roles.some(
    role => !CASE_LOCK_V1_SUBJECT_ROLES.has(role),
  );
  const hasV2NonSubjectRole = v2Roles.some(
    role => !CASE_LOCK_V2_SUBJECT_ROLES.has(role),
  );

  return hasV1NonSubjectRole || hasV2NonSubjectRole;
}

export function isCarrierUserForCaseLock(userRoles: string[]): boolean {
  return userRoles.some(role => CarrierRoles.includes(role));
}

export function isUnderwriterUnassigned(underwriterId: number): boolean {
  return !underwriterId || underwriterId <= 0;
}

export function computeCaseLockAccess(
  input: CaseLockAccessInput,
): CaseLockAccess {
  const {
    isFeatureEnabled,
    userId,
    userRoles,
    underwriterId,
    applicationDetailedLifecycle,
  } = input;

  const isExemptLifecycle = isCaseLockExemptLifecycle(
    applicationDetailedLifecycle,
  );
  const isCarrierUser = isCarrierUserForCaseLock(userRoles);
  const hasNonSubjectRole = hasNonSubjectCaseLockRole(userRoles);
  const isUnassigned = isUnderwriterUnassigned(underwriterId);
  const isAssignedToCurrentUser =
    !isUnassigned && userId != null && Number(underwriterId) === Number(userId);

  if (!isFeatureEnabled) {
    return {
      isFeatureEnabled: false,
      isExemptLifecycle,
      isCarrierUser,
      hasNonSubjectRole,
      isAssignedToCurrentUser,
      isUnassigned,
      isLockedForActions: false,
      bannerMessage: null,
    };
  }

  // With the flag on, actions are allowed for users holding a non-subject role
  // (RBAC still governs what they can do) or when the case is assigned to them.
  // Cases assigned to someone else, or unassigned, are locked.
  const isLockedForActions =
    !isExemptLifecycle &&
    !isCarrierUser &&
    !hasNonSubjectRole &&
    !isAssignedToCurrentUser;

  const bannerMessage = isLockedForActions
    ? isUnassigned
      ? CASE_LOCK_BANNER_UNASSIGNED
      : CASE_LOCK_BANNER_ASSIGNED
    : null;

  return {
    isFeatureEnabled: true,
    isExemptLifecycle,
    isCarrierUser,
    hasNonSubjectRole,
    isAssignedToCurrentUser,
    isUnassigned,
    isLockedForActions,
    bannerMessage,
  };
}
